import React, { useState } from "react";
import { Button } from "@mui/material";
import DISTRICTS from "../../../constants/district";
import PROVINCE from "../../../constants/province";
import WARD from "../../../constants/ward";
import styles from "./OrderItem.module.scss";
import dayjs from "dayjs";
import { PACKAGE_TYPE, PACKAGE_TYPE_VER_2, colorPaymentStatus, paymentStatus } from "../../../constants/constant";
import donationApi from "../../../apis/donationApi";
import { useSnackbar } from "notistack";
import ModalAssignDriver from "../../ModalAssignDriver";
import ModalCompleteOrder from "../ModalCompleteOrder/ModalCompleteOrder";

interface OrderItemProps {
  order: any;
  handleReload: () => void;
  drivers?: any[];
}
const OrderItem = (props: OrderItemProps) => {
  const { handleReload, drivers } = props;
  const { enqueueSnackbar } = useSnackbar();
  const [openDriverModal, setOpenDriverModal] = useState(false);
  const [openCompleteModal, setOpenCompleteModal] = useState(false);

  const parseAddress = (address: any) => {
    const province = PROVINCE.find(
      (p: any) => p.code === address?.provinceID
    ) || { label: "" };

    const district = DISTRICTS.find(
      (d: any) => d.code === address?.districtID
    ) || { name: "" };

    const ward = WARD.find((w: any) => w.code === address?.wardID) || {
      name: "",
    };

    return `${address?.address}, ${ward.name}, ${district.name}, ${province.label}`;
  };

  const handleConfirmDonation = async (id: string) => {
    try {
      const res = await donationApi.confirmDonationShipment({ id });
      if (res?.data?.data) {
        handleReload();
        enqueueSnackbar("Xác nhận thành công", { variant: "success" });
      } else {
        enqueueSnackbar(res.data.message, { variant: "error" });
      }
    } catch (error) {
      console.log(error);
    }
  };
  const handlePostDonationShipment = async (id: string) => {
    try {
      const res = await donationApi.postDonationShipment({ id });
      if (res?.data?.data) {
        handleReload();
        enqueueSnackbar("Cập nhật thành công", { variant: "success" });
      } else {
        enqueueSnackbar(res.data.message, { variant: "error" });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const handleDoneDonation = async (id: string) => {
    try {
      const res = await donationApi.doneDonation({ id });
      if (res?.data?.data) {
        handleReload();
        enqueueSnackbar("Hoàn thành đơn hàng", { variant: "success" });
      } else {
        enqueueSnackbar(res.data.message, { variant: "error" });
      }
    } catch (error) {
      console.log(error);
    }
  };

  const isDriver = localStorage.getItem("userRole") === "driver";

  // Distinct background colors per status so shippers don't miss orders
  const getStatusBgColor = (status: string) => {
    switch (status) {
      case "new":
        return "#fff8e1"; // Light Amber / Warning
      case "confirm":
        return "#e3f2fd"; // Light Blue / Ready to pickup
      case "processing":
        return "#ede7f6"; // Light Purple / In progress
      case "done":
        return "#e8f5e9"; // Light Green / Completed
      default:
        return "#ffffff";
    }
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case "new":
        return <span style={{ padding: "3px 8px", borderRadius: 4, backgroundColor: "#ffb300", color: "#fff", fontWeight: "bold", fontSize: 12 }}>Chờ xác nhận</span>;
      case "confirm":
        return <span style={{ padding: "3px 8px", borderRadius: 4, backgroundColor: "#1e88e5", color: "#fff", fontWeight: "bold", fontSize: 12 }}>Đã xác nhận (Cần lấy)</span>;
      case "processing":
        return <span style={{ padding: "3px 8px", borderRadius: 4, backgroundColor: "#8e24aa", color: "#fff", fontWeight: "bold", fontSize: 12 }}>Đang lấy hàng</span>;
      case "done":
        return <span style={{ padding: "3px 8px", borderRadius: 4, backgroundColor: "#43a047", color: "#fff", fontWeight: "bold", fontSize: 12 }}>Hoàn thành</span>;
      default:
        return <span>{status}</span>;
    }
  };

  return (
    <tr style={{ backgroundColor: getStatusBgColor(props.order.status), transition: "background-color 0.3s" }}>
      <td width="30%">
        <p><b>Mã đơn:</b> {props.order.code}</p>
        <p style={{ margin: "6px 0" }}>
          <b>Trạng thái:</b> {getStatusBadge(props.order.status)}
        </p>

        {!isDriver && (
          <p>
            Trạng thái thanh toán:{" "}
            <span style={{ color: colorPaymentStatus(props.order.paymentStatus) }}>
              {paymentStatus(props.order.paymentStatus)}
            </span>
          </p>
        )}

        <p>
          <b>Ngày tạo:</b> {dayjs(props.order.createdAt).format("DD/MM/YYYY HH:mm")}
        </p>
        {props.order.customerNote && (
          <p><b>Ghi chú:</b> {props.order.customerNote}</p>
        )}
        <p>
          <b>Trọng lượng hàng:</b>{" "}
          <span style={{ color: "#d32f2f", fontWeight: "bold" }}>
            {(() => {
              if (!props.order.services || props.order.services.length === 0) {
                const rootPt = props.order.packageType;
                const foundRoot = PACKAGE_TYPE.find((p: any) => p.packageType === rootPt);
                const foundRoot2 = PACKAGE_TYPE_VER_2.find((p: any) => p.packageTypeId === rootPt);
                return foundRoot?.label || foundRoot2?.label || rootPt;
              }

              let totalMin = 0;
              let totalMax = 0;
              let hasValidWeight = false;

              props.order.services.forEach((svc: any) => {
                const pt = svc.packageType || (svc as any).package_type;
                const pId = (svc as any).packageTypeId || (svc as any).packageTypeID;
                let label = "";
                if (pt) {
                  label = PACKAGE_TYPE.find((p: any) => p.packageType === pt)?.label || pt;
                } else if (pId) {
                  label = PACKAGE_TYPE_VER_2.find((p: any) => p.packageTypeId === pId)?.label || pId;
                }

                if (label) {
                  const nums = label.match(/\d+/g);
                  if (nums && nums.length >= 2) {
                    totalMin += parseInt(nums[0], 10);
                    totalMax += parseInt(nums[1], 10);
                    hasValidWeight = true;
                  }
                }
              });

              if (hasValidWeight) {
                return `${totalMin} đến ${totalMax} kg`;
              }

              // Fallback
              const rootPt = props.order.packageType;
              const foundRoot = PACKAGE_TYPE.find((p: any) => p.packageType === rootPt);
              const foundRoot2 = PACKAGE_TYPE_VER_2.find((p: any) => p.packageTypeId === rootPt);
              return foundRoot?.label || foundRoot2?.label || rootPt;
            })()}
          </span>
        </p>
        <p style={{ margin: "4px 0" }}>
          <b>{isDriver ? "Ngày nhận hàng:" : "Ngày dự kiến nhận hàng:"}</b>{" "}
          <span style={{ fontWeight: isDriver ? "bold" : "normal", color: isDriver ? "#0f172a" : "inherit" }}>
            {dayjs(props.order.collectDate).format("DD/MM/YYYY")}
          </span>
        </p>
        {(props.order.collectFromTime || props.order.collectToTime) && (
          <div
            style={
              isDriver
                ? {
                    marginTop: 6,
                    marginBottom: 6,
                    padding: "4px 8px",
                    backgroundColor: "#f0f9ff",
                    borderRadius: 6,
                    border: "1px solid #bae6fd",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }
                : { margin: "4px 0" }
            }
          >
            <span style={{ fontWeight: "bold", color: isDriver ? "#0369a1" : "inherit", fontSize: isDriver ? 13 : "inherit" }}>
              {isDriver ? "⏰ Giờ nhận hàng:" : "Giờ nhận hàng dự kiến:"}{" "}
            </span>
            <span
              style={
                isDriver
                  ? {
                      backgroundColor: "#0284c7",
                      color: "#ffffff",
                      fontWeight: "bold",
                      fontSize: 14,
                      padding: "2px 8px",
                      borderRadius: 4,
                      letterSpacing: "0.5px",
                    }
                  : { color: "#0288d1", fontWeight: "bold" }
              }
            >
              {props.order.collectFromTime ? props.order.collectFromTime.slice(0, 5) : ""}{" "}
              - {props.order.collectToTime ? props.order.collectToTime.slice(0, 5) : ""}
            </span>
          </div>
        )}

        {isDriver ? (
          <div style={{ marginTop: 8, padding: "6px 10px", backgroundColor: "#e8f5e9", borderRadius: 6, border: "1px dashed #43a047" }}>
            <span style={{ fontWeight: "bold", color: "#2e7d32", fontSize: 14 }}>
              Số tiền nhận được:{" "}
              {(() => {
                try {
                  const storedConfigs = JSON.parse(localStorage.getItem("driver_order_incomes") || "{}");
                  const conf = storedConfigs[props.order.id];
                  if (props.order.driverIncome !== undefined && props.order.driverIncome !== null) {
                    return Number(props.order.driverIncome).toLocaleString("vi") + " VNĐ";
                  }
                  if (conf?.calculatedIncome !== undefined && conf?.calculatedIncome !== null) {
                    return Number(conf.calculatedIncome).toLocaleString("vi") + " VNĐ";
                  }
                } catch (e) {}
                return "0 VNĐ";
              })()}
            </span>
          </div>
        ) : (
          <>
            <p>
              Giá vận chuyển: {props.order.shippingPrice.toLocaleString("vi")} VNĐ
            </p>
            <p>
              Tổng tiền: {props.order.totalPrice.toLocaleString("vi")} VNĐ
            </p>
          </>
        )}
      </td>
      <td width={isDriver ? "25%" : "20%"}>
        <p><b>Tên:</b> {props.order.customerName}</p>
        <p style={{ margin: "4px 0" }}>
          <b>Sđt:</b>{" "}
          {props.order.customerPhone ? (
            <a
              href={`tel:${props.order.customerPhone.toString().replace(/[^0-9+]/g, "")}`}
              title="Nhấn để gọi ngay"
              style={{
                color: "#0d47a1",
                fontWeight: "bold",
                display: "inline-flex",
                alignItems: "center",
                gap: "4px",
                padding: "3px 8px",
                backgroundColor: "#e3f2fd",
                border: "1px solid #bbdefb",
                borderRadius: "6px",
                textDecoration: "none",
                cursor: "pointer",
              }}
            >
              📞 <span>{props.order.customerPhone}</span>
            </a>
          ) : (
            <span>-</span>
          )}
        </p>
        {!isDriver && props.order.customerEmail && (
          <p><b>Email:</b> {props.order.customerEmail}</p>
        )}
      </td>
      <td width={isDriver ? "30%" : "25%"}>
        <p>{parseAddress(props.order?.customerAddress)}</p>
      </td>
      {!isDriver && (
        <td width="15%">
          {(() => {
            const activeDriver = props.order.driverId && drivers ? drivers.find(d => d.id === props.order.driverId) : null;
            if (activeDriver) {
              return (
                <div>
                  <p style={{ fontWeight: "bold", color: "#e65100", marginBottom: 4 }}>
                    {activeDriver.name}
                  </p>
                  {(() => {
                    try {
                      const storedConfigs = JSON.parse(localStorage.getItem("driver_order_incomes") || "{}");
                      const conf = storedConfigs[props.order.id];
                      const hasIncome = props.order.driverIncome !== undefined || conf?.calculatedIncome !== undefined;
                      const income = props.order.driverIncome !== undefined ? props.order.driverIncome : conf?.calculatedIncome;
                      if (hasIncome) {
                        return (
                          <span style={{ fontSize: "12px", color: income > 0 ? "#2e7d32" : "#555", fontWeight: 600, display: "inline-block", backgroundColor: income > 0 ? "#e8f5e9" : "#f5f5f5", padding: "2px 6px", borderRadius: 4 }}>
                            Thù lao: {Number(income).toLocaleString("vi-VN")} đ
                          </span>
                        );
                      }
                    } catch (e) {}
                    return null;
                  })()}
                </div>
              );
            }
            return <p style={{ color: "#94a3b8", fontStyle: "italic" }}>Chưa gán</p>;
          })()}
        </td>
      )}
      <td>
        {props.order.status && props.order.status === "new" && (
          <Button
            size="small"
            variant="contained"
            color="success"
            onClick={() => handleConfirmDonation(props.order.id)}
          >
            Xác nhận
          </Button>
        )}
        {props.order.status && props.order.status === "confirm" && (
          <Button
            size="small"
            variant="contained"
            color="success"
            onClick={() => handlePostDonationShipment(props.order.id)}
            style={{ marginBottom: 5 }}
          >
            Đang lấy hàng
          </Button>
        )}
        {props.order.status && props.order.status === "processing" && (
          <Button
            size="small"
            variant="contained"
            color="success"
            onClick={() => {
              if (isDriver) {
                setOpenCompleteModal(true);
              } else {
                handleDoneDonation(props.order.id);
              }
            }}
            style={{ marginBottom: 5 }}
          >
            Hoàn thành
          </Button>
        )}
        {(props.order.status === "new" || props.order.status === "confirm" || props.order.status === "processing") && localStorage.getItem("userRole") !== "driver" && (() => {
          const isAssigned = Boolean(props.order.driverId && drivers && drivers.some(d => d.id === props.order.driverId));
          return (
            <Button
              size="small"
              variant={isAssigned ? "contained" : "outlined"}
              color="primary"
              onClick={() => setOpenDriverModal(true)}
              style={{ marginTop: 4, textTransform: "none" }}
            >
              {isAssigned ? "Sửa tài xế / giá" : "Thêm tài xế"}
            </Button>
          );
        })()}
        
        {openDriverModal && (
          <ModalAssignDriver
            open={openDriverModal}
            handleClose={() => setOpenDriverModal(false)}
            refresh={handleReload}
            orderId={props.order.id}
            order={props.order}
          />
        )}

        {openCompleteModal && (
          <ModalCompleteOrder
            open={openCompleteModal}
            handleClose={() => setOpenCompleteModal(false)}
            order={props.order}
            onSuccess={handleReload}
          />
        )}
      </td>
    </tr>
  );
};

export default OrderItem;
